import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { FaFacebook, FaGoogle, FaInstagram } from "react-icons/fa";

const domainConfig = {
    'PPC': {
        icon: <FaGoogle size={12} />,
        color: 'text-green-700',
        bgColor: 'bg-green-50/60',
        borderColor: 'border-green-200/40',
        hoverBg: 'hover:bg-green-50'
    },
    'Facebook': {
        icon: <FaFacebook size={12} />,
        color: 'text-blue-700',
        bgColor: 'bg-blue-50/60',
        borderColor: 'border-blue-200/40',
        hoverBg: 'hover:bg-blue-50'
    },
    'More': {
        icon: <FaInstagram size={12} />,
        color: 'text-pink-700',
        bgColor: 'bg-pink-50/60',
        borderColor: 'border-pink-200/40',
        hoverBg: 'hover:bg-pink-50'
    }
};

// Fallback config for unknown domains
const getFallbackConfig = (domain) => ({
    icon: <AlertTriangle size={12} />,
    color: 'text-gray-700',
    bgColor: 'bg-gray-50/60',
    borderColor: 'border-gray-200/40',
    hoverBg: 'hover:bg-gray-50'
});

export default function DomainOverview({ pages, hasNoPages }) {
    const [hoveredDomain, setHoveredDomain] = useState(null);

    // DEBUG: Log pages data to diagnose issue
    console.log('[DomainOverview] Received pages:', pages);
    console.log('[DomainOverview] hasNoPages:', hasNoPages);

    if (hasNoPages) {
        return (
            <div className="flex items-center justify-center gap-2 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle size={16} className="text-red-600" />
                <span className="text-sm font-semibold text-red-700">No pages configured for this store</span>
            </div>
        );
    }

    // Group pages by domain with defensive checks
    const pagesByDomain = pages.reduce((acc, page) => {
        // DEBUG: Log each page's domain
        if (!page.domain || typeof page.domain !== 'string') {
            console.warn('[DomainOverview] Page with invalid/missing domain:', page);
        }

        const domain = page.domain || 'Unknown';
        if (!acc[domain]) {
            acc[domain] = [];
        }
        acc[domain].push(page);
        return acc;
    }, {});

    // DEBUG: Log grouped domains
    console.log('[DomainOverview] Grouped domains:', Object.keys(pagesByDomain));

    return (
        <div className="flex items-center gap-2 mb-4">
            {Object.entries(pagesByDomain).map(([domain, domainPages]) => {
                // Get config with fallback for unknown domains
                const config = domainConfig[domain] || getFallbackConfig(domain);

                // DEBUG: Warn about unknown domains
                if (!domainConfig[domain]) {
                    console.warn('[DomainOverview] Unknown domain encountered:', domain, 'Pages:', domainPages);
                }

                const domainStatus = domainPages.some(p => p.status === 'error') ? 'error' :
                    domainPages.some(p => p.status === 'pending') ? 'pending' : 'clean';

                return (
                    <div
                        key={domain}
                        onMouseEnter={() => setHoveredDomain(domain)}
                        onMouseLeave={() => setHoveredDomain(null)}
                        className={`
                            flex items-center gap-1.5 px-3 py-2 rounded-lg border flex-1 justify-center
                            transition-all duration-200 cursor-default
                            ${config.bgColor} ${config.borderColor} ${config.hoverBg}
                            ${hoveredDomain === domain ? 'shadow-sm scale-105' : ''}
                        `}
                    >
                        <div className={`${config.color} transition-transform duration-200 ${hoveredDomain === domain ? 'scale-110' : ''}`}>
                            {config.icon}
                        </div>
                        <span className={`text-xs font-semibold ${config.color}`}>
                            {domain}
                        </span>
                        <span className="text-xs font-normal text-slate-600">
                            ({domainPages.length})
                        </span>
                        <div className={`w-1 h-1 rounded-full flex-shrink-0 transition-all duration-200 ${domainStatus === 'error' ? 'bg-red-500' :
                            domainStatus === 'pending' ? 'bg-amber-500' : 'bg-emerald-500'
                            } ${hoveredDomain === domain ? 'scale-125' : ''}`} />
                    </div>
                );
            })}
        </div>
    );
}
